import type { SizedGridColumn } from "@glideapps/glide-data-grid";
import { getAiSpreadsheetCellDisplayValue } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/cell-display-value";
import { pointHeightToPixels } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/row-resize";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import { coordinateKey } from "./types";

const DEFAULT_COLUMN_WIDTH = 140;
export const DEFAULT_ROW_HEIGHT = 28;
const CHARACTER_WIDTH_PX = 7;
const COLUMN_WIDTH_PADDING_PX = 8;
const MIN_COLUMN_WIDTH_PX = 56;
const LINE_HEIGHT_PX = 17;
const ROW_VERTICAL_PADDING_PX = 12;
const APPROX_CHAR_WIDTH_PX = 6.5;
const CELL_HORIZONTAL_PADDING_PX = 16;
const MAX_AUTO_ROW_LINES = 4;

const getDimensionValue = (
  dimensions: Record<string, number> | number[] | null,
  oneBasedIndex: number,
  fallback: number,
) => {
  const value = Array.isArray(dimensions)
    ? dimensions[oneBasedIndex - 1]
    : dimensions?.[String(oneBasedIndex)];

  return value ?? fallback;
};

export const getColumnWidth = (
  columnWidths: Record<string, number> | number[] | null,
  oneBasedIndex: number,
) => {
  const characters = getDimensionValue(columnWidths, oneBasedIndex, NaN);

  if (Number.isNaN(characters)) return DEFAULT_COLUMN_WIDTH;

  return Math.max(
    MIN_COLUMN_WIDTH_PX,
    Math.round(characters * CHARACTER_WIDTH_PX) + COLUMN_WIDTH_PADDING_PX,
  );
};

export const pixelWidthToCharacterUnits = (pixels: number) =>
  Math.max((pixels - COLUMN_WIDTH_PADDING_PX) / CHARACTER_WIDTH_PX, 0);

export const getColumnTitle = (index: number) => {
  let columnNumber = index + 1;
  let title = "";

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    title = String.fromCharCode(65 + remainder) + title;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return title;
};

const getStoredRowHeight = (
  rowHeights: Record<string, number> | number[] | null,
  oneBasedIndex: number,
): number | undefined => {
  const points = Array.isArray(rowHeights)
    ? rowHeights[oneBasedIndex - 1]
    : rowHeights?.[String(oneBasedIndex)];

  if (points === undefined || points === null) return undefined;

  return pointHeightToPixels(points);
};

const estimateWrappedLines = (text: string, columnWidth: number): number => {
  const usableWidth = Math.max(columnWidth - CELL_HORIZONTAL_PADDING_PX, 1);
  const lines = Math.ceil((text.length * APPROX_CHAR_WIDTH_PX) / usableWidth);

  return Math.min(Math.max(lines, 1), MAX_AUTO_ROW_LINES);
};

export const buildRowHeights = (
  sheet: IAiSpreadsheetLoadedSheet,
  columns: SizedGridColumn[],
  rowCount: number,
  cellsByCoordinate: Map<string, IAiSpreadsheetCell>,
): Map<number, number> => {
  const heights = new Map<number, number>();

  for (let row = 1; row <= rowCount; row++) {
    const storedHeight = getStoredRowHeight(sheet.row_heights, row);
    if (storedHeight !== undefined) {
      heights.set(row - 1, storedHeight);
      continue;
    }
    let lines = 1;

    columns.forEach((column, columnIndex) => {
      const cell = cellsByCoordinate.get(coordinateKey(columnIndex + 1, row));

      if (cell?.format?.alignment?.wrap_text) {
        lines = Math.max(
          lines,
          estimateWrappedLines(
            getAiSpreadsheetCellDisplayValue(cell),
            column.width,
          ),
        );
      }
    });

    const wrappedHeight =
      lines > 1 ? lines * LINE_HEIGHT_PX + ROW_VERTICAL_PADDING_PX : 0;

    heights.set(row - 1, Math.max(DEFAULT_ROW_HEIGHT, wrappedHeight));
  }

  return heights;
};
